import { initLazyImages } from '~/scripts/utils/helpers/lazy-media.js';

/**
 * <infinite-collection>
 *
 * Portad fran advance-watch och skeleton-theme sa att alla teman kor samma
 * komponent. Skillnaderna mot brittfurns tidigare egna variant, och varfor de
 * spelar roll har:
 *
 * 1. Custom element. disconnectedCallback river ner sig sjalv nar noden tas
 *    bort, och den nya noden startar sig sjalv. Pa kollektionssidorna byter
 *    filtren ut hela #CollectionAjaxContent, sa den tidigare varianten slutade
 *    fungera efter forsta filterklicket och behovde en egen lyssnare pa
 *    collection:reloaded. Det behovs inte langre, elementet laker sig sjalvt.
 *
 * 2. Observern tittar pa det fjarde kortet fran slutet, inte pa en vakt langst
 *    ner. Vakten ligger efter samtliga kort, vilket pa en tvakolumners
 *    mobilgrid ar flera skarmhojder bort, sa kunden fick scrolla nastan hela
 *    vagen innan nasta sida ens borjade hamtas.
 *
 * 3. AbortController. Klickar kunden in pa en produkt mitt i en hamtning
 *    avbryts den i stallet for att slutforas mot en sida hon lamnat.
 *
 * 4. Pagineringen ar en riktig lankalista (.js-pagination, visuellt dold) som
 *    alltid ligger i DOM:en, i stallet for att gommas i <noscript>. Sokmotorer
 *    som kor JS foljer den, vilket noscript inte garanterar.
 *
 * Markup renderas av snippets/bf2-infinite-scroll.liquid. Kortselektorn kan
 * overstyras med data-card-selector, sa bloggen och kollektionslistan kan peka
 * ut sina egna kort.
 */
const CONSTANTS = {
    PAGINATION_SELECTOR: '.js-pagination',
    NEXT_PAGE_LINK_SELECTOR: '[data-next-page-link]',
    LOAD_MORE_SELECTOR: '[data-infinite-more]',
    STATUS_SELECTOR: '[data-infinite-status]',
    DEFAULT_CARD_SELECTOR: '.bf2-product',
    ROOT_MARGIN_MULTIPLIER: 1.5,
    LOAD_MORE_CARD_OFFSET: 4
};

class InfiniteCollection extends HTMLElement {
    connectedCallback() {
        this.init();
        this.bfcacheHandler = this.bfcacheHandler || this.handleBfcache.bind(this);
        window.addEventListener('pageshow', this.bfcacheHandler);
    }

    disconnectedCallback() {
        this.destroy();
        window.removeEventListener('pageshow', this.bfcacheHandler);
    }

    get cardSelector() {
        return this.dataset.cardSelector || CONSTANTS.DEFAULT_CARD_SELECTOR;
    }

    /**
     * Var korten bor. Pa bf2-kollektionen ligger rutnatet inuti elementet. De
     * aldre sektionerna (bloggen, kollektionslistan) har sitt rutnat utanfor,
     * och pekar ut det med data-grid i stallet for att byggas om. Selektorn
     * slas upp fran narmaste forfader forst, sa att ett subkollektionsrutnat
     * med samma klass inte traffas av misstag.
     */
    get cardRoot() {
        const sel = this.dataset.grid;
        if (!sel) return this;
        if (this.gridEl && this.gridEl.isConnected) return this.gridEl;
        let node = this.parentElement;
        while (node) {
            const hit = node.querySelector(sel);
            if (hit) { this.gridEl = hit; return hit; }
            node = node.parentElement;
        }
        this.gridEl = document.querySelector(sel);
        return this.gridEl || this;
    }

    init() {
        this.pagination = this.querySelector(CONSTANTS.PAGINATION_SELECTOR);
        if (!this.pagination) return;

        this.nextPageLink = this.pagination.querySelector(CONSTANTS.NEXT_PAGE_LINK_SELECTOR);
        this.status = this.querySelector(CONSTANTS.STATUS_SELECTOR);
        this.loadMoreBtn = this.querySelector(CONSTANTS.LOAD_MORE_SELECTOR);
        this.results = this.cardRoot.querySelectorAll(this.cardSelector);
        this.controller = null;
        this.isFetching = false;

        // Avbryt pagaende hamtning nar kunden klickar in pa ett kort.
        this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
        this.addEventListener('click', this.resultClickHandler);

        if (this.loadMoreBtn) {
            this.loadMoreHandler = this.loadMoreHandler || ((evt) => {
                if (!this.nextPageLink) return;
                evt.preventDefault();
                this.loadMore();
            });
            this.loadMoreBtn.addEventListener('click', this.loadMoreHandler);
        }

        if (this.nextPageLink && this.results.length > 0) this.initObserver();
    }

    initObserver() {
        if (this.paginationObserver) this.paginationObserver.disconnect();

        this.paginationObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !this.isFetching) this.loadMore();
                });
            },
            { rootMargin: `${window.innerHeight * CONSTANTS.ROOT_MARGIN_MULTIPLIER}px 0px` }
        );

        this.paginationObserver.observe(this.getObserverTarget());
    }

    /**
     * Elementet vars synlighet startar nasta hamtning. Fjarde kortet fran
     * slutet, sa laddningen hinner igang innan kunden nar botten. Faller
     * tillbaka pa pagineringen nar det finns for fa kort.
     */
    getObserverTarget() {
        const cards = this.results;
        if (cards && cards.length > 0) {
            return cards[Math.max(0, cards.length - CONSTANTS.LOAD_MORE_CARD_OFFSET)];
        }
        return this.pagination;
    }

    destroy() {
        if (this.paginationObserver) {
            this.paginationObserver.disconnect();
            this.paginationObserver = null;
        }
        if (this.controller) {
            this.controller.abort();
            this.controller = null;
        }
        this.removeEventListener('click', this.resultClickHandler);
        if (this.loadMoreBtn && this.loadMoreHandler) {
            this.loadMoreBtn.removeEventListener('click', this.loadMoreHandler);
        }
    }

    reload() {
        this.destroy();
        this.init();
    }

    handleBfcache(evt) {
        if (evt.persisted) this.reload();
    }

    handleResultClick(evt) {
        if (evt.target.closest('a') && evt.target.closest(this.cardSelector)) {
            if (this.controller) {
                this.controller.abort();
                this.controller = null;
            }
        }
    }

    setStatus(visible) {
        if (this.status) this.status.classList.toggle('js-hidden', !visible);
    }

    async loadMore() {
        if (this.isFetching || this.dataset.pauseInfiniteScroll === 'true') return;

        this.isFetching = true;
        this.setStatus(true);
        this.pagination?.classList.add('is-loading');
        this.controller = new AbortController();

        try {
            if (!this.nextPageLink) throw new Error('Ingen nasta sida.');

            const response = await fetch(this.nextPageLink.href, { signal: this.controller.signal });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            if (this.dataset.pauseInfiniteScroll === 'true') throw new Error('Avbruten.');

            this.updateElements(await response.text());
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('InfiniteCollection: kunde inte hamta nasta sida', error);
            }
        } finally {
            this.pagination?.classList.remove('is-loading');
            this.setStatus(false);
            this.controller = null;
            this.isFetching = false;
        }
    }

    updateElements(html) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = html;

        const lastResult = this.results[this.results.length - 1];
        if (!lastResult || !lastResult.isConnected) return;

        let newResultsHtml = '';
        tmpl.content.querySelectorAll(this.cardSelector).forEach((result) => {
            newResultsHtml += result.outerHTML;
        });
        lastResult.insertAdjacentHTML('afterend', newResultsHtml);

        // bf2-korten bar riktiga bildadresser, aldre rutnat kor data-src.
        initLazyImages();

        this.results = this.cardRoot.querySelectorAll(this.cardSelector);

        const newPagination = tmpl.content.querySelector(CONSTANTS.PAGINATION_SELECTOR);
        if (newPagination && this.pagination) {
            this.pagination.innerHTML = newPagination.innerHTML;
            this.nextPageLink = this.pagination.querySelector(CONSTANTS.NEXT_PAGE_LINK_SELECTOR);

            if (!this.nextPageLink) {
                this.pagination.remove();
                if (this.loadMoreBtn) this.loadMoreBtn.remove();
                this.destroy();
            } else {
                if (this.loadMoreBtn) this.loadMoreBtn.setAttribute('href', this.nextPageLink.href);
                this.initObserver();
            }
        }
    }
}

if (!customElements.get('infinite-collection')) {
    customElements.define('infinite-collection', InfiniteCollection);
}
